import React, { useMemo, useEffect, useRef } from "react";
import { QuestionnaireProvider } from "context/questionnaire";
import { useQuestionnaire } from "context/questionnaire";
import QuestionnaireLayout from "layouts/questionnaire-layout";
import QuestionnairePage from "pages/questionnaire";
import { QuestionnaireLanguagePicker } from "components/questionnaire-language-picker";
import { Pendo } from "components/pendo";

declare global {
  interface Window {
    pendo?: { track?: (name: string, properties: Record<string, unknown>) => void };
  }
}

const TRACK_EVENT = "pp_questionnaire_page";

const QuestionnairePendo: React.FC = () => {
  const { questionnaire, isLoading, currentPageIndex, locale, isComplete } =
    useQuestionnaire();
  const prevPageRef = useRef<number>(-1);

  const account = useMemo(() => {
    const orgId = questionnaire?.metadata?.organization_id;
    if (!orgId) return {};
    return { id: orgId };
  }, [questionnaire?.metadata?.organization_id]);

  useEffect(() => {
    if (!questionnaire || currentPageIndex < 0) return;
    if (currentPageIndex === prevPageRef.current) return;

    const wasLanding = prevPageRef.current === -1;
    prevPageRef.current = currentPageIndex;

    window.pendo?.track?.(TRACK_EVENT, {
      survey_id: questionnaire.id,
      survey_name: questionnaire.name,
      visible_page_index: currentPageIndex,
      locale,
      phase: wasLanding ? "started" : "in_progress",
    });
  }, [questionnaire, currentPageIndex, locale]);

  useEffect(() => {
    if (!isComplete || !questionnaire) return;

    window.pendo?.track?.(TRACK_EVENT, {
      survey_id: questionnaire.id,
      survey_name: questionnaire.name,
      locale,
      phase: "submitted",
    });
  }, [isComplete, questionnaire, locale]);

  return <Pendo account={account} isDataLoading={isLoading} />;
};

const QuestionnaireContent: React.FC = () => {
  const { questionnaire, progress, currentPageIndex, restartQuestionnaire } =
    useQuestionnaire();
  const isLandingPage = currentPageIndex === -1;

  return (
    <QuestionnaireLayout
      progress={progress}
      logoUrl={questionnaire?.theme?.logo_url}
      logoMaxHeight={questionnaire?.theme?.logo_max_height}
      showProgress={!isLandingPage}
      showRestart={!isLandingPage}
      onRestart={restartQuestionnaire}
      languagePicker={<QuestionnaireLanguagePicker />}
    >
      <QuestionnairePage />
    </QuestionnaireLayout>
  );
};

const QuestionnaireRoutes: React.FC = () => {
  return (
    <QuestionnaireProvider>
      <QuestionnairePendo />
      <QuestionnaireContent />
    </QuestionnaireProvider>
  );
};

export default QuestionnaireRoutes;
